"use client";

import { Home } from "lucide-react";
import React from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";

import { useDashboard } from "./context/DashboardContext";

/* ---------------- UI HELPERS ---------------- */

function Card({ className = "", children }) {
  return (
    <div
      className={`rounded-2xl shadow-md p-4 ${className} transition transform hover:scale-[1.02] hover:shadow-lg`}
    >
      {children}
    </div>
  );
}

function CardContent({ className = "", children }) {
  return <div className={`w-full h-full ${className}`}>{children}</div>;
}

/* ---------------- DASHBOARD ---------------- */

export default function Dashboard() {
  const { dashboard, loading, error } = useDashboard();

  if (loading) return <div className="p-6">Loading Dashboard...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!dashboard) return null;

  // 🔹 API DATA
  const ordersData = dashboard.weeklyOrders || [];
  const usersData = dashboard.weeklyUsers || [];
  const revenueData = dashboard.weeklyRevenue || [];

  // 🔹 Pie Chart (users distribution by week)
  const pieData = usersData.map((item) => ({
    name: item.week,
    value: item.total,
  }));

  // 🔹 Line Chart (orders vs users)
  const lineData = ordersData.map((order, index) => ({
    week: order.week,
    orders: order.total,
    users: usersData[index]?.total || 0,
  }));

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
          <Home className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-teal-800">
          Dashboard - {dashboard.month}/{dashboard.year}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">

        {/* Print Jobs (Orders) */}
        <Card className="bg-[#d9d4eb] border border-orange-400 shadow-2xl hover:-translate-y-2">
          <CardContent>
            <h2 className="text-xl font-semibold mb-4">Print Jobs per Week</h2>
            <div className="h-64 rounded-xl bg-white/30">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ordersData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#c3b1e1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Users Pie */}
        <Card className="border border-orange-400 shadow-2xl hover:-translate-y-2">
          <CardContent>
            <h2 className="text-xl font-semibold mb-4">
              Users Distribution by Week
            </h2>
            <div className="h-64 rounded-xl bg-white/30">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" outerRadius={90}>
                    {pieData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={["#a8dadc", "#ffe5d9", "#ffd6a5", "#caffbf"][i % 4]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue */}
        <Card className="lg:col-span-2 border border-orange-400 shadow-2xl hover:-translate-y-2">
          <CardContent>
            <h2 className="text-3xl font-semibold mb-4 text-teal-800">
              Revenue per Week
            </h2>
            <div className="h-72 rounded-xl bg-yellow-50">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#a0c4ff" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Orders vs Users Trend */}
        <Card className="lg:col-span-2 bg-pink-100 border border-orange-400 shadow-2xl hover:-translate-y-2">
          <CardContent>
            <h2 className="text-xl font-semibold mb-4">
              Orders vs Users Trend
            </h2>
            <div className="h-72 rounded-xl bg-white/30">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Line dataKey="orders" stroke="#ffadad" strokeWidth={2} />
                  <Line dataKey="users" stroke="#9bf6ff" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}